<script setup lang="ts">
import { computed } from 'vue'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'

const MAX_VISIBLE_POSTS = 3

const props = defineProps<{
    pageName?: string | null
    title?: string | null
    posts?: any[]
}>()

const posts = computed(() => Array.isArray(props.posts) ? props.posts : [])
const visiblePosts = computed(() => posts.value.slice(0, MAX_VISIBLE_POSTS))
const hiddenPostsCount = computed(() => Math.max(posts.value.length - visiblePosts.value.length, 0))
const title = computed(() => props.title || props.pageName || 'Seção importada')
const pageName = computed(() => props.pageName || props.title || 'Importada')

const stripHtml = (value?: string) => (typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '')
const getPostTitle = (post: any) => stripHtml(post?.title) || 'Sem título'
const getPostImage = (post: any) => post?.mainImage ?? post?.image?.url ?? post?.image ?? ''
const focalPoint = (post: any) => post?.mainImageExtra?.background_image_focal_point ?? { x: 50, y: 50 }

const formatDate = (date?: string) => {
    if (!date) return ''

    const parsed = new Date(`${date}T00:00:00`)

    if (Number.isNaN(parsed.getTime())) return ''

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(parsed)
}
</script>

<template>
    <div class="beta-imported-preview" @pointerdown.stop>
        <div class="beta-imported-preview__badge">
            <span>Seção importada de:</span>
            <strong>{{ pageName }}</strong>
        </div>

        <div class="beta-imported-preview__header">
            <div class="beta-imported-preview__heading">
                <h2 class="beta-imported-preview__title">{{ title }}</h2>
            </div>
        </div>

        <div v-if="visiblePosts.length" class="beta-imported-preview__cards">
            <article
                v-for="(post, index) in visiblePosts"
                :key="post?.postId ?? post?.id ?? index"
                class="beta-imported-preview__card"
            >
                <div class="beta-imported-preview__media">
                    <beta-base-image
                        v-if="getPostImage(post)"
                        :src="getPostImage(post)"
                        :alt="getPostTitle(post)"
                        class-name="beta-imported-preview__image"
                        :style="{
                            objectPosition: `${focalPoint(post).x}% ${focalPoint(post).y}%`,
                        }"
                    />

                    <div v-else class="beta-imported-preview__placeholder">
                        Sem capa
                    </div>
                </div>

                <div class="beta-imported-preview__card-content">
                    <time v-if="formatDate(post?.display_date)" class="beta-imported-preview__date">
                        {{ formatDate(post.display_date) }}
                    </time>

                    <h3 class="beta-imported-preview__card-title">
                        {{ getPostTitle(post) }}
                    </h3>
                </div>
            </article>

            <div v-if="hiddenPostsCount > 0" class="beta-imported-preview__count">
                +{{ hiddenPostsCount }} itens
            </div>
        </div>

        <div v-else class="beta-imported-preview__empty">
            Conteúdo importado sem itens para pré-visualizar.
        </div>
    </div>
</template>

<style scoped>
.beta-imported-preview {
    position: relative;
    display: flex;
    max-height: 420px;
    min-height: 260px;
    width: 100%;
    flex-direction: column;
    gap: 18px;
    overflow: hidden;
    border: 1px solid rgba(202, 213, 229, 0.82);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.72);
    padding: 24px;
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
}

.beta-imported-preview__badge {
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 999px;
    background: rgba(248, 250, 252, 0.86);
    padding: 6px 12px;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
}

.beta-imported-preview__badge strong {
    max-width: 220px;
    overflow: hidden;
    color: #1f2937;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-imported-preview__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.beta-imported-preview__heading {
    min-width: 0;
}

.beta-imported-preview__title {
    margin: 0;
    color: #111827;
    font-size: 32px;
    font-weight: 800;
    line-height: 1.08;
    overflow-wrap: anywhere;
}

.beta-imported-preview__cards {
    display: grid;
    min-height: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

.beta-imported-preview__card,
.beta-imported-preview__count {
    min-width: 0;
    overflow: hidden;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    background: #fff;
}

.beta-imported-preview__media {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: #ececec;
}

:deep(.beta-imported-preview__image) {
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
}

.beta-imported-preview__placeholder {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 800;
}

.beta-imported-preview__card-content {
    padding: 10px 12px 12px;
}

.beta-imported-preview__date {
    display: block;
    margin-bottom: 6px;
    color: #64748b;
    font-size: 11px;
    line-height: 1.2;
}

.beta-imported-preview__card-title {
    display: -webkit-box;
    margin: 0;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: #111827;
    font-size: 14px;
    font-weight: 800;
    line-height: 1.2;
    overflow: hidden;
}

.beta-imported-preview__count {
    display: flex;
    min-height: 124px;
    align-items: center;
    justify-content: center;
    border-style: dashed;
    color: #64748b;
    font-size: 18px;
    font-weight: 800;
}

.beta-imported-preview__empty {
    display: flex;
    min-height: 124px;
    align-items: center;
    justify-content: center;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #64748b;
    font-size: 14px;
    font-weight: 700;
}

@media (max-width: 768px) {
    .beta-imported-preview {
        max-height: 420px;
        padding: 18px;
    }

    .beta-imported-preview__header {
        flex-direction: column;
    }

    .beta-imported-preview__title {
        font-size: 24px;
    }

    .beta-imported-preview__cards {
        grid-template-columns: minmax(0, 1fr);
        overflow-y: auto;
    }
}
</style>
